import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';

const AddStaff = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    employeeId: '',
    contactNumber: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    staffType: '',
    department: '',
    specialization: '',
    photo: null,
    status: 'Active'
  });

  // Course assignments state
  const [courseAssignments, setCourseAssignments] = useState([
    {
      id: 1,
      courseName: '',
      courseSearchQuery: '',
      selectedModuleId: '',
      canTeachPrimary: true,
      canTeachAssistant: true
    }
  ]);

  // Module data for dropdowns
  const [modulesList, setModulesList] = useState([]);
  const [filteredModules, setFilteredModules] = useState({});
  const [showModuleDropdowns, setShowModuleDropdowns] = useState({});
  const [loading, setLoading] = useState(false);

  // Load modules on component mount
  useEffect(() => {
    const loadModules = async () => {
      if (!currentUser) return;
      
      try {
        // Get admin's university_id
        let universityId = currentUser?.university_id;
        
        if (!universityId && currentUser?.id) {
          // If university_id not in user context, get from admins table
          const { data: adminData, error: adminError } = await supabase
            .from('admins')
            .select('university_id')
            .eq('user_id', currentUser.id)
            .single();
          
          if (!adminError && adminData) {
            universityId = adminData.university_id;
          }
        }

        if (!universityId) {
          console.error('Unable to determine university_id for loading modules');
          return;
        }

        console.log('Loading modules for university:', universityId);

        // Load modules from Supabase
        const { data: modulesData, error: modulesError } = await supabase
          .from('modules')
          .select('id, module_name, module_code, active')
          .eq('university_id', universityId)
          .eq('active', true)
          .order('module_name', { ascending: true });

        if (modulesError) {
          console.error('Error loading modules:', modulesError);
          return;
        }

        console.log(`Loaded ${modulesData?.length || 0} modules for university ${universityId}`);
        setModulesList(modulesData || []);
        
        // Initialize filtered modules for existing course assignments
        const initialFiltered = {};
        courseAssignments.forEach(assignment => {
          initialFiltered[assignment.id] = modulesData || [];
        });
        setFilteredModules(initialFiltered);
        
      } catch (error) {
        console.error('Error loading modules:', error);
      }
    };

    if (currentUser) {
      loadModules();
    }
  }, [currentUser]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      photo: file
    }));
  };

  // Handle course assignment changes
  const handleCourseAssignmentChange = (id, field, value) => {
    setCourseAssignments(prev => 
      prev.map(assignment => 
        assignment.id === id 
          ? { ...assignment, [field]: value }
          : assignment
      )
    );
  };

  // Handle module search for course assignments
  const handleModuleSearch = (assignmentId, query) => {
    setCourseAssignments(prev => 
      prev.map(assignment => 
        assignment.id === assignmentId 
          ? { ...assignment, courseSearchQuery: query }
          : assignment
      )
    );
    
    if (query.trim() === '') {
      setFilteredModules(prev => ({ ...prev, [assignmentId]: modulesList }));
    } else {
      const filtered = modulesList.filter(module => 
        module.module_name.toLowerCase().includes(query.toLowerCase()) ||
        module.module_code.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredModules(prev => ({ ...prev, [assignmentId]: filtered }));
    }
    setShowModuleDropdowns(prev => ({ ...prev, [assignmentId]: true }));
  };

  // Handle module selection for course assignments
  const handleModuleSelect = (assignmentId, module) => {
    console.log('Selected module for assignment:', assignmentId, module);
    setCourseAssignments(prev => 
      prev.map(assignment => 
        assignment.id === assignmentId 
          ? { 
              ...assignment, 
              courseName: module.module_name,
              courseSearchQuery: `${module.module_name} (${module.module_code})`,
              selectedModuleId: module.id 
            }
          : assignment
      )
    );
    setShowModuleDropdowns(prev => ({ ...prev, [assignmentId]: false }));
  };

  // Add new course assignment
  const addCourseAssignment = () => {
    const newId = Math.max(...courseAssignments.map(c => c.id), 0) + 1;
    setCourseAssignments(prev => [
      ...prev,
      {
        id: newId,
        courseName: '',
        courseSearchQuery: '',
        selectedModuleId: '',
        canTeachPrimary: true,
        canTeachAssistant: true
      }
    ]);
    // Initialize dropdown state for new assignment
    setFilteredModules(prev => ({ ...prev, [newId]: modulesList }));
  };

  // Remove course assignment
  const removeCourseAssignment = (id) => {
    if (courseAssignments.length > 1) {
      setCourseAssignments(prev => prev.filter(assignment => assignment.id !== id));
      // Clean up dropdown states
      setFilteredModules(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
      setShowModuleDropdowns(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employeeId) {
      alert('Employee ID is required');
      return;
    }

    if (!formData.fullName) {
      alert('Full Name is required');
      return;
    }

    if (!formData.email) {
      alert('Email is required');
      return;
    }

    if (!formData.password) {
      alert('Password is required');
      return;
    }

    if (!formData.staffType) {
      alert('Staff Type is required');
      return;
    }

    if (!currentUser) {
      alert('Please log in to add staff');
      return;
    }

    try {
      setLoading(true);

      // Get admin's university_id
      let universityId = currentUser?.university_id;
      
      if (!universityId && currentUser?.id) {
        const { data: adminData, error: adminError } = await supabase
          .from('admins')
          .select('university_id')
          .eq('user_id', currentUser.id)
          .single();
        
        if (!adminError && adminData) {
          universityId = adminData.university_id;
        }
      }

      if (!universityId) {
        alert('Unable to determine your university. Please contact support.');
        return;
      }

      console.log('Adding staff for university:', universityId);
      console.log('University ID type and value:', typeof universityId, universityId);

      // Prepare staff data for Supabase
      const staffData = {
        name: formData.fullName,                    // Full Name -> name
        email: formData.email,                      // Email -> email  
        password: formData.password,                // Password -> password
        employee_id: formData.employeeId,           // Employee ID -> employee_id
        contact_no: formData.contactNumber,         // Contact Number -> contact_no
        dob: formData.dateOfBirth || null,          // Date of Birth -> dob
        gender: formData.gender || null,            // Gender -> gender
        address: formData.address || null,          // Address -> address
        staff_type: formData.staffType,             // Staff Type -> staff_type
        department: formData.department || null,    // Department -> department
        specialization: formData.specialization || null, // Specialization -> specialization
        active: formData.status === 'Active',       // Status -> active (boolean)
        university_id: universityId,                // Admin's university_id
        photo_url: null, // TODO: Handle photo upload later
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Staff data being submitted to Supabase:', staffData);
      console.log('Specific staff_type value:', JSON.stringify(formData.staffType));

      // Insert staff into Supabase
      const { data: staffResult, error: staffError } = await supabase
        .from('staff')
        .insert([staffData])
        .select()
        .single();

      if (staffError) {
        console.error('Error inserting staff:', staffError);
        console.error('Full error details:', JSON.stringify(staffError, null, 2));
        throw staffError;
      }

      console.log('Staff created successfully:', staffResult);
      const staffId = staffResult.id;

      // Insert course assignments if any
      const validCourseAssignments = courseAssignments.filter(assignment => 
        assignment.courseName.trim() !== '' && assignment.selectedModuleId
      );

      if (validCourseAssignments.length > 0) {
        console.log('Adding course assignments for staff:', staffId);
        
        const courseAssignmentPromises = validCourseAssignments.map(async (assignment) => {
          const courseData = {
            staff_id: staffId,                              // Foreign key to staff table
            course_name: assignment.courseName,             // Course/Module Name -> course_name
            module_id: assignment.selectedModuleId,         // ✅ NEW: Required module_id foreign key
            can_teach_as_primary: assignment.canTeachPrimary,   // Can Teach as Primary -> can_teach_as_primary
            can_teach_as_assistant: assignment.canTeachAssistant, // Can Teach as Assistant -> can_teach_as_assistant
            created_at: new Date().toISOString()
          };

          console.log('Course assignment data:', courseData);

          const { data, error } = await supabase
            .from('staff_courses')
            .insert([courseData])
            .select()
            .single();

          if (error) {
            console.error('Error inserting course assignment:', error);
            throw error;
          }

          return data;
        });

        // Wait for all course assignments to complete
        const courseResults = await Promise.all(courseAssignmentPromises);
        console.log('All course assignments created successfully:', courseResults);
      }

      alert('Staff member added successfully!');
      navigate('/admin', { state: { refresh: true } });

    } catch (error) {
      console.error('Error adding staff:', error);
      let errorMessage = 'Error adding staff: ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error.details) {
        errorMessage += error.details;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {!currentUser ? (
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Please Log In
            </h2>
            <p style={{fontSize: 16, fontFamily: 'Outfit', color: '#77838F'}}>
              You need to be logged in as an admin to add staff.
            </p>
          </div>
        ) : (
        
        /* Staff Details Section */
        <form onSubmit={handleSubmit}>
          {loading && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: 'white',
                padding: '20px 40px',
                borderRadius: 8,
                textAlign: 'center',
                fontSize: 16,
                fontFamily: 'Outfit',
                color: '#F04915'
              }}>
                Adding staff member...
              </div>
            </div>
          )}
          
          <div style={{margin: '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: '20px', color: 'white'}}>
              <h2 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>Staff Details</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: '40px'}}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* Left Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Full Name <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="fullName"
                      type="text"
                      placeholder="John Doe"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Email <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Password <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="password"
                      type="password"
                      placeholder="***********"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Employee ID <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="employeeId"
                      type="text"
                      placeholder="EMP001"
                      value={formData.employeeId}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Contact Number
                    </label>
                    <input
                      name="contactNumber"
                      type="tel"
                      placeholder="+1234567890"
                      value={formData.contactNumber}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Date of Birth
                    </label>
                    <input
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Staff Type <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="staffType"
                      value={formData.staffType}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Staff Type</option>
                      <option value="lecturer">Lecturer</option>
                      <option value="professor">Professor</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Department
                    </label>
                    <input
                      name="department"
                      type="text"
                      placeholder="Computer Science"
                      value={formData.department}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Specialization
                    </label>
                    <input
                      name="specialization"
                      type="text"
                      placeholder="Machine Learning, Database Systems"
                      value={formData.specialization}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Status <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Photo
                    </label>
                    <div style={{width: '100%', height: 120, border: '1px dashed #77838F', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'}}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        style={{opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer'}}
                      />
                      <div style={{color: '#77838F', fontSize: 16, fontFamily: 'Outfit', fontWeight: '400', textAlign: 'center'}}>
                        Click to upload photo
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address - Full Width */}
                <div style={{gridColumn: '1 / -1', marginTop: '20px'}}>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Address
                  </label>
                  <textarea
                    name="address"
                    placeholder="Enter full address..."
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    style={{
                      width: '100%', 
                      padding: '15px', 
                      borderRadius: 8, 
                      border: '1px solid #77838F', 
                      fontSize: 16, 
                      fontFamily: 'Outfit',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Course Assignments Section */}
          <div style={{margin: '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: '20px', color: 'white'}}>
              <h2 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>Course Assignments</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: '40px'}}>
              {courseAssignments.map((assignment, index) => (
                <div key={assignment.id} style={{
                  marginBottom: index === courseAssignments.length - 1 ? '0' : '30px',
                  padding: '20px',
                  border: '1px solid #E0E0E0',
                  borderRadius: 8,
                  background: 'white'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                    <h3 style={{fontSize: 18, fontFamily: 'Outfit', fontWeight: '600', color: '#333', margin: 0}}>
                      Course Assignment {index + 1}
                    </h3>
                    {courseAssignments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCourseAssignment(assignment.id)}
                        style={{
                          background: '#DC3545',
                          border: 'none',
                          borderRadius: 6,
                          color: 'white',
                          padding: '8px 12px',
                          fontSize: 14,
                          fontFamily: 'Outfit',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = '#C82333';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = '#DC3545';
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '20px', alignItems: 'end'}}>
                    {/* Course/Module Name */}
                    <div style={{position: 'relative'}}>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Course/Module Name <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search for module..."
                        value={assignment.courseSearchQuery}
                        onChange={(e) => handleModuleSearch(assignment.id, e.target.value)}
                        onFocus={() => {
                          setShowModuleDropdowns(prev => ({ ...prev, [assignment.id]: true }));
                          if (!filteredModules[assignment.id]) {
                            setFilteredModules(prev => ({ ...prev, [assignment.id]: modulesList }));
                          }
                        }}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: assignment.selectedModuleId ? '2px solid #28a745' : '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: assignment.selectedModuleId ? '#f8fff8' : 'white'
                        }}
                      />
                      {assignment.selectedModuleId && (
                        <div style={{
                          fontSize: 12,
                          color: '#28a745',
                          marginTop: '2px',
                          fontFamily: 'Outfit'
                        }}>
                          ✓ Selected: {assignment.courseName}
                        </div>
                      )}
                      {showModuleDropdowns[assignment.id] && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #77838F',
                          borderTop: 'none',
                          borderRadius: '0 0 8px 8px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}>
                          {(filteredModules[assignment.id] || []).length === 0 ? (
                            <div style={{
                              padding: '12px 15px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#888',
                              textAlign: 'center'
                            }}>
                              No modules found for your university
                            </div>
                          ) : (
                            (filteredModules[assignment.id] || []).map(module => (
                              <div
                                key={module.id}
                                onClick={() => handleModuleSelect(assignment.id, module)}
                                style={{
                                  padding: '10px 15px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  fontSize: 14,
                                  fontFamily: 'Outfit',
                                  transition: 'background-color 0.2s',
                                  backgroundColor: assignment.selectedModuleId === module.id ? '#e3f2fd' : 'white'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.backgroundColor = '#f8f9fa';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.backgroundColor = assignment.selectedModuleId === module.id ? '#e3f2fd' : 'white';
                                }}
                              >
                                {module.module_name} ({module.module_code})
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {/* Can Teach as Primary */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Can Teach as Primary
                      </label>
                      <div style={{display: 'flex', alignItems: 'center', height: 49}}>
                        <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer', marginRight: '20px'}}>
                          <input
                            type="radio"
                            name={`primary_${assignment.id}`}
                            checked={assignment.canTeachPrimary === true}
                            onChange={() => handleCourseAssignmentChange(assignment.id, 'canTeachPrimary', true)}
                            style={{marginRight: '8px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>Yes</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                          <input
                            type="radio"
                            name={`primary_${assignment.id}`}
                            checked={assignment.canTeachPrimary === false}
                            onChange={() => handleCourseAssignmentChange(assignment.id, 'canTeachPrimary', false)}
                            style={{marginRight: '8px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>No</span>
                        </label>
                      </div>
                    </div>

                    {/* Can Teach as Assistant */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Can Teach as Assistant
                      </label>
                      <div style={{display: 'flex', alignItems: 'center', height: 49}}>
                        <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer', marginRight: '20px'}}>
                          <input
                            type="radio"
                            name={`assistant_${assignment.id}`}
                            checked={assignment.canTeachAssistant === true}
                            onChange={() => handleCourseAssignmentChange(assignment.id, 'canTeachAssistant', true)}
                            style={{marginRight: '8px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>Yes</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                          <input
                            type="radio"
                            name={`assistant_${assignment.id}`}
                            checked={assignment.canTeachAssistant === false}
                            onChange={() => handleCourseAssignmentChange(assignment.id, 'canTeachAssistant', false)}
                            style={{marginRight: '8px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Course Button */}
              <div style={{marginTop: '20px', textAlign: 'center'}}>
                <button
                  type="button"
                  onClick={addCourseAssignment}
                  style={{
                    background: '#28A745',
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    padding: '12px 24px',
                    fontSize: 16,
                    fontFamily: 'Outfit',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#218838';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#28A745';
                  }}
                >
                  <span style={{fontSize: '18px'}}>+</span>
                  Add Course
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{margin: '40px', display: 'flex', justifyContent: 'space-between'}}>
            <Link to="/admin">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back
              </button>
            </Link>
            <button 
              type="submit" 
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#D63A0F';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#F04915';
              }}
            >
              Submit
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default AddStaff;
